import useSWR from "swr";

import { ApiClientFactory } from "common/middleware/api.client-factory";
import {
  ISharedOpportunityLookupDto,
  OfferDto,
  SharedOpportunityStatus,
} from "common/middleware/api.services";
import { Logger } from "common/middleware/logger";

const getSharedOpportunities = async (
  key: string,
  stateFilters: SharedOpportunityStatus[],
  page: number,
  pageSize: number,
  retrieveAll: boolean,
  getCurrentNegotiationTerms: boolean,
) => {
  Logger.debug("Getting shared opportunities");

  const client = await ApiClientFactory.getSharedOpportunitiesClient();

  const res = await client.getPage(
    stateFilters,
    undefined,
    undefined,
    undefined,
    page,
    undefined,
    pageSize,
    retrieveAll,
  );

  const opportunities: {
    totalNumOfOpportunities: number,
    entities: (ISharedOpportunityLookupDto & {
      currentNegotiationTerms?: OfferDto,
    })[],
  } = {
    totalNumOfOpportunities: res.count,
    entities: res.entities,
  };

  if (getCurrentNegotiationTerms) {
    const purchaseOfferClient = await ApiClientFactory.getPurchaseOffersClient();

    opportunities.entities = await Promise.all(opportunities.entities.map(async (o) => {
      // An opportunity may not have a deal yet (e.g. browsing stage), and getCurrentOffer
      // throws on a missing dealId. Guard it, and never let one bad opportunity reject the
      // whole list — otherwise the opportunities page would be stuck on skeletons forever.
      if (!o.dealId) {
        return { ...o, currentNegotiationTerms: undefined };
      }

      try {
        const terms = await purchaseOfferClient.getCurrentOffer(o.dealId, true);

        return {
          ...o,
          currentNegotiationTerms: terms,
        }
      } catch (e) {
        Logger.error(e, `Failed to load current offer for deal ${o.dealId}`);

        return { ...o, currentNegotiationTerms: undefined };
      }
    }));
  }

  return opportunities;
}

export const useSharedOpportunities = (opt?: {
  page?: number,
  pageSize?: number,
  retrieveAll?: boolean,
  stateFilters?: SharedOpportunityStatus[],
  getCurrentNegotiationTerms?: boolean,
}) => {
  const { data, error, mutate } = useSWR(
    [
      "/api/sharedOpportunities",
      opt?.stateFilters,
      opt?.page,
      opt?.pageSize,
      opt?.retrieveAll,
      opt?.getCurrentNegotiationTerms,
    ],
    getSharedOpportunities
  );

  return {
    opportunities: data,
    isOpportunitiesLoading: !data && !error,
    error,
    mutateOpportunities: mutate,
  }
}